/** Em-dash separator used between service level and transit days. */
const SERVICE_LEVEL_SEPARATOR = "-";

// Format the date in UTC so that an API value like `2026-05-05T00:00:00Z`
// (which represents a calendar date, not a local instant) renders as `5/5/2026`
// regardless of the viewer's timezone.
const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "numeric",
	day: "numeric",
	year: "numeric",
	timeZone: "UTC",
});

// Format the date in the viewer's resolved timezone (no `timeZone` option) so
// that API values representing true UTC instants — e.g. a stop's
// `actualTimestamp` of `2026-05-05T03:00:00Z` — render as the wall-clock date
// the user actually experienced (`5/4/2026` in `America/New_York`).
const localShortDateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "numeric",
	day: "numeric",
	year: "numeric",
});

/**
 * Returns the trimmed string, or `null` for null/undefined/empty/whitespace.
 * Drives the hide-on-missing decision for the Carrier, PRO #, and Agent PRO
 * cells.
 */
export function getDisplayValue(
	value: string | null | undefined,
): string | null {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();
	return trimmed === "" ? null : trimmed;
}

/**
 * Formats an ISO date string as `M/d/yyyy` (en-US, no leading zeros).
 * Returns `null` for null/undefined/empty/whitespace/unparseable input so the
 * Expiration Date cell can be omitted from the DOM.
 */
export function formatShortDate(
	value: string | null | undefined,
): string | null {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();
	if (trimmed === "") {
		return null;
	}

	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return shortDateFormatter.format(date);
}

/**
 * Formats an ISO UTC instant as `M/d/yyyy` in the viewer's resolved timezone
 * (en-US, no leading zeros). Use this for true UTC instants such as a stop's
 * `actualTimestamp` / `scheduledDate` on the LTL drawer so the displayed date
 * matches the user's local wall-clock. For values that represent calendar
 * dates rather than instants (e.g. expiration dates), use {@link formatShortDate}
 * instead. Returns `null` for null/undefined/empty/whitespace/unparseable input.
 */
export function formatLocalShortDate(
	value: string | null | undefined,
): string | null {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();
	if (trimmed === "") {
		return null;
	}

	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return localShortDateFormatter.format(date);
}

/**
 * Formats a transit-days count as `n Day` (singular) or `n Days` (plural).
 * Returns `null` when the input is null/undefined.
 */
export function formatTransitDays(
	transitDays: number | null | undefined,
): string | null {
	if (transitDays == null) {
		return null;
	}

	const suffix = transitDays === 1 ? "Day" : "Days";
	return `${String(transitDays)} ${suffix}`;
}

export function formatServiceLevelWithDays(
	serviceLevel: string | null | undefined,
	abbreviation: string | null | undefined,
	transitDays: number | null | undefined,
): string | null {
	const serviceLevelText = getDisplayValue(serviceLevel);
	const abbreviationText = getDisplayValue(abbreviation);
	const transitDaysText = formatTransitDays(transitDays);

	if (serviceLevelText === null && transitDaysText === null) {
		return null;
	}

	let levelPart: string | null = null;
	if (serviceLevelText !== null) {
		levelPart =
			abbreviationText === null
				? serviceLevelText
				: `(${abbreviationText}) ${serviceLevelText}`;
	}

	if (levelPart !== null && transitDaysText !== null) {
		return `${levelPart} ${SERVICE_LEVEL_SEPARATOR} ${transitDaysText}`;
	}

	return levelPart ?? transitDaysText;
}

/**
 * Formats a phone number as `(NNN) NNN-NNNN`, optionally suffixed by
 * ` ext. EEE`. Returns the raw input when the digit count is unrecognized.
 */
function formatPhoneDisplay(
	phone: string,
	extension: string | null | undefined,
): string {
	const digits = phone.replaceAll(/\D/g, "");
	let formatted: string;
	if (digits.length === 10) {
		formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	} else if (digits.length === 11 && digits.startsWith("1")) {
		formatted = `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	} else {
		formatted = phone;
	}

	const ext = getDisplayValue(extension);
	return ext === null ? formatted : `${formatted} ext. ${ext}`;
}

export function formatContactNameAndPhone(
	name: string | null | undefined,
	phone: string | null | undefined,
	extension: string | null | undefined,
	wrapLongValues = true,
): string {
	const nameValue = getDisplayValue(name);
	const phoneValue = getDisplayValue(phone);

	const phoneDisplay =
		phoneValue === null ? null : formatPhoneDisplay(phoneValue, extension);

	if (nameValue === null && phoneDisplay === null) {
		return "";
	}
	if (nameValue === null) {
		return phoneDisplay ?? "";
	}
	if (phoneDisplay === null) {
		return nameValue;
	}
	const separator =
		wrapLongValues && nameValue.length + 1 + phoneDisplay.length > 21
			? "\n"
			: " ";
	return `${nameValue}${separator}${phoneDisplay}`;
}
